import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';

interface Status {
  activated_at?: string;
  configured: boolean;
  counts?: Record<string, number>;
  credentials_updated_at?: string;
  enabled?: boolean;
  error_code?: string;
  failures?: { id: string; state: string; error_code: string }[];
  fax_number?: string;
  last_success_at?: string;
  password_set?: boolean;
  pending_extraction?: number;
  routing_verified?: boolean;
}
export function FaxStatus() {
  const { user, organization } = useAuth();
  const org = organization?.organization_id;
  const base = `/api/v1/organizations/${org}/fax`;
  const cache = useQueryClient();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmRouting, setConfirmRouting] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);
  const status = useQuery({
    enabled: !!org,
    refetchInterval: 30000,
    queryKey: ['fax-status', org, user?.id],
    queryFn: async () => (await clientApi.get<Status>(base + '/status')).data,
  });
  if (status.isError)
    return <p role="status">Fax connection status is unavailable. Existing Inbox documents remain available.</p>;
  const data = status.data;
  if (!data?.configured) return null;
  async function saveCredentials() {
    setSavingSecret(true);
    setError('');
    try {
      await clientApi.put(`${base}/credentials`, { password, routing_verified: confirmRouting });
      setPassword('');
      await cache.invalidateQueries({ queryKey: ['fax-status', org, user?.id] });
    } catch {
      setError('Unable to save the SRFax password.');
    } finally {
      setSavingSecret(false);
    }
  }

  return (
    <section aria-label="Fax connection" className="flex flex-col gap-2 rounded-lg border p-4">
      <p>
        SRFax · {data.fax_number} ·{' '}
        {!data.enabled ? 'Paused' : !data.activated_at ? 'Preparing new-fax baseline' : 'New faxes enabled'}
      </p>
      <p>
        Last successful check:{' '}
        {data.last_success_at ? new Date(data.last_success_at).toLocaleString() : 'Not connected yet'}
      </p>
      <p>
        {(data.counts?.pending || 0) + (data.counts?.retry || 0)} awaiting import · {data.counts?.needs_review || 0}{' '}
        require review · {data.pending_extraction || 0} awaiting text extraction dispatch
      </p>
      {/*
        The password is entered here, per clinic, and stored encrypted. It used
        to live in a JSON file on the server holding every tenant's, which meant
        connecting a clinic was an ops ticket and one file compromised all of
        them.
      */}
      {(!data.password_set || !data.routing_verified) && (
        <div className="flex flex-col gap-2 rounded-md border border-dashed p-3">
          <p className="text-sm font-medium">
            {data.password_set ? 'Confirm the routing to start importing' : 'Finish connecting SRFax'}
          </p>
          <label htmlFor="srfax-password" className="flex flex-col gap-1 text-sm">
            SRFax password
            <input
              id="srfax-password"
              type="password"
              value={password}
              autoComplete="off"
              className="rounded-md border px-2 py-1.5"
              placeholder={data.password_set ? 'Replace the stored password' : 'Paste the SRFax account password'}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label htmlFor="srfax-routing" className="flex items-start gap-2 text-sm">
            <input
              id="srfax-routing"
              type="checkbox"
              className="mt-1"
              checked={confirmRouting}
              onChange={(e) => setConfirmRouting(e.target.checked)}
            />
            <span>
              I have confirmed that {data.fax_number} belongs to this clinic. SRFax returns the whole account inbox
              and we filter it to this number, so getting it wrong imports another practice&rsquo;s faxes.
            </span>
          </label>
          <Button disabled={!password || !confirmRouting || savingSecret} onClick={saveCredentials}>
            {savingSecret ? 'Saving…' : 'Save password'}
          </Button>
        </div>
      )}
      {data.password_set && data.routing_verified && data.credentials_updated_at && (
        <p className="text-sm text-muted-foreground">
          Password set {new Date(data.credentials_updated_at).toLocaleDateString()}. It is stored encrypted and never
          shown again.
        </p>
      )}
      {data.error_code && <p role="alert">Connection requires attention: {data.error_code.replaceAll('_', ' ')}</p>}
      {!!data.failures?.length && (
        <details>
          <summary>Import issues</summary>
          {data.failures.map((item) => (
            <div key={item.id} className="flex items-center gap-3">
              <span>{item.error_code.replaceAll('_', ' ')}</span>
              <Button
                disabled={busy}
                variant="outline"
                onClick={async () => {
                  setBusy(true);
                  setError('');
                  try {
                    await clientApi.post(`${base}/imports/${item.id}/retry`);
                    await cache.invalidateQueries({ queryKey: ['fax-status', org] });
                  } catch {
                    setError('Retry could not be requested. Check your upload permission or ask an administrator.');
                  } finally {
                    setBusy(false);
                  }
                }}>
                Retry import
              </Button>
            </div>
          ))}
        </details>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
